import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../components/header";
import "./activateFirm.css";
import { authenticatedFetch } from "../../../network/api";

const FirmActivationPage = () => {
  const navigate = useNavigate();
  const [autoActivateLawyers, setAutoActivateLawyers] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);

  const user = useSelector((s) => s.attorneyData.attorneyData);
  const firm = useSelector((s) => s.firm.details);

  const handleActivate = async () => {
    if (!firm?.id) return;
    setActivating(true);
    setError(null);
    try {
      const res = await authenticatedFetch("/api/attorney/webapp/firm/activate", {
        method: "POST",
        body: JSON.stringify({ firmId: firm.id, cascadeLawyers: autoActivateLawyers }),
      });
      const data = await res.json();
      if (!res.ok || data?.error) {
        throw new Error(data?.error || "Activation failed");
      }
      navigate("/onboarding/success-firm");
    } catch (e) {
      setError(e.message || "Activation failed. Please try again.");
    } finally {
      setActivating(false);
    }
  };

  const handleReviewProfile = () => {
    navigate("/firm/profile");
  };

  const handleViewGuidelines = () => {
    navigate("/help/visibility");
  };

  const checklistItems = [
    { text: "Firm name, logo, and practice areas configured", completed: true },
    { text: "Service locations and timezone properly set", completed: true },
    { text: "Compliance disclaimer added (if required)", completed: false, manual: true }
  ];

  const faqItems = [
    {
      question: "What information becomes publicly visible?",
      answer: "Your firm name, logo, description, practice areas, service locations, and profiles of activated attorneys will be visible to potential clients browsing the marketplace."
    },
    {
      question: "Can I deactivate my firm later?",
      answer: "Yes, you can deactivate your firm or individual attorney profiles at any time through your admin dashboard. Changes take effect immediately."
    },
    {
      question: "How does attorney activation work?",
      answer: "Individual attorneys can control their own visibility settings. If you choose to auto-activate them, they'll receive notifications and can opt-out if desired."
    }
  ];

  return (
    <div className="firm-activation-container">
      <Header />

      <main className="firm-activation-main" role="main">
        <section className="hero-section-activate">
          <div className="eyebrow">Final Step</div>
          <h1 className="hero-title-activate">Go live on Quidpro Marketplace</h1>
          <p className="hero-subtitle-activate">
            Make your firm discoverable to potential clients. This will also activate your admin account and enable all marketplace features.
          </p>
        </section>

        <section className="card-activate centered-card">
          <h2 className="section-title">Pre-activation Checklist</h2>
          <ul className="checklist">
            {checklistItems.map((item, index) => (
              <li 
                key={index} 
                className={`checklist-item ${item.completed ? 'auto' : 'manual'}`}
              >
                {item.text}
              </li>
            ))}
          </ul>
          <button 
            className="link-button" 
            onClick={handleReviewProfile}
            type="button"
          >
            Review firm profile →
          </button>
        </section>

        <section className="card-activate preview-card">
          <h2 className="section-title">Marketplace Preview</h2>
          <div className="preview-placeholder">
            Your firm listing will appear here
          </div>
        </section>

        <section className="card-activate callout-card">
          <div className="callout-header">
            <div className="callout-icon">🚀</div>
            <div>
              <h3 className="callout-title">Ready to Activate</h3>
              <p className="callout-body">
                Your firm profile and eligible attorneys will become visible in search results immediately upon activation.
              </p>
            </div>
          </div>

          <label className="toggle-row">
            <input
              type="checkbox"
              checked={autoActivateLawyers}
              onChange={(e) => setAutoActivateLawyers(e.target.checked)}
              disabled={activating}
            />
            <span>
              Automatically activate all eligible attorneys (they can opt-out individually later)
            </span>
          </label>

          <div className="actions">
            <button
              className="primary-button"
              onClick={handleActivate}
              disabled={activating || !firm?.id}
              type="button"
            >
              {activating ? "Activating..." : "Activate Firm"}
            </button>
            <button 
              className="link-button" 
              onClick={handleViewGuidelines}
              type="button"
              disabled={activating}
            >
              View visibility guidelines
            </button>
          </div>

          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}
        </section>

        <section className="card-activate faq-card">
          <h2 className="section-title">Frequently Asked Questions</h2>
          {faqItems.map((item, index) => (
            <details key={index}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </section>
      </main>
    </div>
  );
};

export default FirmActivationPage;